import { MENTION_TYPES, Mention, MentionType, toMention } from './types';

const triggerForMentionType = (type: MentionType): '@' | '#' =>
  type === 'entity' || type === 'theme' || type.startsWith('insight')
    ? '@'
    : '#';

/**
 * Labels (e.g. insight headlines) can contain `]`, which would otherwise be
 * read as the end of the marker's label segment — escape it (and any
 * backslash) so `parseMentionMarkers` round-trips it correctly.
 */
const escapeMarkerLabel = (label: string): string =>
  label.replace(/\\/g, '\\\\').replace(/\]/g, '\\]');

const unescapeMarkerLabel = (label: string): string =>
  label.replace(/\\(.)/g, '$1');

/** `@[label](type:id)` / `#[label](type:id)` — FE marker format. */
export const mentionToMarkerText = (mention: Mention): string =>
  `${triggerForMentionType(mention.type)}[${escapeMarkerLabel(mention.label)}](${mention.type}:${mention.id})`;

const MENTION_TYPE_PATTERN = MENTION_TYPES.join('|');
const MARKER_REGEX = new RegExp(
  String.raw`[@#]\[((?:\\.|[^\\\]])+)\]\((${MENTION_TYPE_PATTERN}):([^)]+)\)`,
  'g'
);

export const parseMentionMarkers = (text: string): (string | Mention)[] => {
  const segments: (string | Mention)[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(MARKER_REGEX)) {
    const [full, rawLabel, type, id] = match;
    const label = unescapeMarkerLabel(rawLabel);
    const index = match.index ?? 0;

    if (index > lastIndex) {
      segments.push(text.slice(lastIndex, index));
    }

    const mention = toMention(type, id, label);
    if (mention) {
      segments.push(mention);
    }
    lastIndex = index + full.length;
  }

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments;
};

export const stripMentionMarkers = (text: string): string =>
  parseMentionMarkers(text)
    .map(segment => (typeof segment === 'string' ? segment : segment.label))
    .join('');

/**
 * Plain-text mention display:
 * - flag on → labels only (`aapl`)
 * - flag off → keep typed wire markers (`@[aapl](entity:aapl_nasd)`)
 */
export const toMentionPlainText = (
  text: string,
  mentionsEnabled: boolean
): string => (mentionsEnabled ? stripMentionMarkers(text) : text);
